import React, { useState, useEffect } from 'react';
import { Box, Paper, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Typography, Divider, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Components
import MeetingDashboard from './MeetingDashboard';
import CreateMeeting from './CreateMeeting';
import MeetingList from './MeetingList';
import MeetingOrganizers from './MeetingOrganizers';
import MeetingConfiguration from './MeetingConfiguration';
import MeetingAttendeesReport from './MeetingAttendeesReport';
import api from '../../api';

const MeetingManagement = () => {
  const theme = useTheme();
  // Safe fallback if `dashboard` theme properties are missing
  const dashboard = theme.dashboard || {
    paper: { minHeight: '85vh', borderRadius: 2, marginTop: 2 },
    sidebar: { width: 260, paddingTop: 2, paddingBottom: 2, paddingX: 1 },
    content: { padding: 3 },
    navItem: { marginBottom: 1, paddingX: 1, paddingY: 1, borderRadius: 2, iconMinWidth: 40, primaryFontSize: '0.9rem', secondaryFontSize: '0.75rem' },
    sectionHeading: { paddingX: 2, paddingY: 1, color: 'text.secondary', fontWeight: 'bold', marginTop: 1, fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase' },
    divider: { marginY: 2, marginX: 2 }
  };
  
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  const empId = localStorage.getItem('emp_id');
  const userRole = localStorage.getItem('role') || '';
  const isAdminOrHR = ['admin', 'super admin', 'hr', 'hr admin', 'management'].some(r => userRole.toLowerCase().includes(r));

  useEffect(() => {
    if (isAdminOrHR) {
      setAuthorized(true); setLoading(false); return;
    }
    api.get(`/meeting-views/is-organizer/${empId}`)
      .then(res => { setAuthorized(res.data.is_organizer); setLoading(false); })
      .catch(() => { setAuthorized(false); setLoading(false); });
  }, [empId, isAdminOrHR]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!authorized) return <Typography variant="h5" color="error" sx={{ p: 5, textAlign: 'center' }}>Access Denied</Typography>;

  const NavGroupHeader = ({ title }) => (
    <Typography variant="caption" sx={{ px: dashboard.sectionHeading.paddingX, py: dashboard.sectionHeading.paddingY, display: 'block', color: dashboard.sectionHeading.color, fontWeight: dashboard.sectionHeading.fontWeight, mt: dashboard.sectionHeading.marginTop, fontSize: dashboard.sectionHeading.fontSize, letterSpacing: dashboard.sectionHeading.letterSpacing, textTransform: dashboard.sectionHeading.textTransform }}>
      {title}
    </Typography>
  );

  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton selected={activeMenu === id} onClick={() => setActiveMenu(id)} sx={{ borderRadius: dashboard.navItem.borderRadius, mx: dashboard.navItem.paddingX, py: dashboard.navItem.paddingY, '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }, '&.Mui-selected .MuiListItemIcon-root': { color: 'white' } }}>
        <ListItemIcon sx={{ minWidth: dashboard.navItem.iconMinWidth, color: activeMenu === id ? 'white' : 'inherit' }}>{icon}</ListItemIcon>
        <ListItemText primary={label} primaryTypographyProps={{ fontWeight: activeMenu === id ? 'bold' : 'medium', fontSize: dashboard.navItem.primaryFontSize }} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Paper elevation={2} sx={{ display: 'flex', height: dashboard.paper.minHeight, width: '100%', borderRadius: dashboard.paper.borderRadius, overflow: 'hidden', mt: dashboard.paper.marginTop }}>
      
      {/* LEFT SIDEBAR - Independent scrolling */}
      <Box sx={{ width: dashboard.sidebar.width, minWidth: dashboard.sidebar.width, borderRight: 1, borderColor: 'divider', bgcolor: '#ffffff', pt: dashboard.sidebar.paddingTop, pb: dashboard.sidebar.paddingBottom, px: dashboard.sidebar.paddingX, boxShadow: '1px 0 3px rgba(0,0,0,0.04)', zIndex: 1, overflowY: 'auto', overflowX: 'hidden', height: '100%', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-track': { background: '#f1f1f1' }, '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: 2 }, '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' } }}>
        <List component="nav" dense sx={{ py: 0 }}>
          <NavGroupHeader title="MEETING DASHBOARD" />
          <NavItem id="dashboard" label="Overview" icon={<DashboardIcon fontSize="small" />} />
          <NavItem id="create" label="Register Meeting" icon={<AddCircleIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />
          <NavGroupHeader title="MY WORKSPACE" />
          <NavItem id="my_meetings" label="My Meetings" icon={<ListAltIcon fontSize="small" />} />

          {isAdminOrHR && (
            <>
              <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />
              <NavGroupHeader title="ADMINISTRATION" />
              <NavItem id="all_meetings" label="All Meetings" icon={<ListAltIcon fontSize="small" />} />
              <NavItem id="attendees_report" label="Attendees Report" icon={<AssessmentIcon fontSize="small" />} />
              <NavItem id="organizers" label="Meeting Organizers" icon={<PeopleIcon fontSize="small" />} />
              <NavItem id="config" label="Master Configuration" icon={<SettingsIcon fontSize="small" />} />
            </>
          )}
        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Independent scrolling */}
      <Box sx={{ flexGrow: 1, p: dashboard.content.padding, width: '100%', bgcolor: '#fbfbfb', overflowY: 'auto', overflowX: 'hidden', height: '100%', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: 3 }, '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: 3 }, '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' } }}>
        {activeMenu === 'dashboard' && <MeetingDashboard viewType={isAdminOrHR ? 'ALL' : 'MY'} />}
        {activeMenu === 'create' && <CreateMeeting />}
        {activeMenu === 'my_meetings' && <MeetingList viewType="MY" />}
        {activeMenu === 'all_meetings' && isAdminOrHR && <MeetingList viewType="ALL" />}
        {activeMenu === 'attendees_report' && isAdminOrHR && <MeetingAttendeesReport />}
        {activeMenu === 'organizers' && isAdminOrHR && <MeetingOrganizers />}
        {activeMenu === 'config' && isAdminOrHR && <MeetingConfiguration />}
      </Box>

    </Paper>
  );
};

export default MeetingManagement;